export const promptMetaTemplate = `# Prompt 元模板標準（Prompt Meta-Template）

> 版本：v1.0
> 用途：為任何新專案生成 Layer Prompt 時，作為「Prompt 的 Prompt」—— 確保每個 Layer 都遵循一致、可驗證的結構。

---

## 元模板結構（7 個標準章節）

每個 Layer Prompt 必須包含以下章節，順序固定：

\`\`\`
# Layer N：<專案名稱> <層次名稱>

> 目標：一句話描述本 Layer 的輸出目標
> 禁止：明確列出本 Layer 不該做的事情
> 輸入：前置條件（哪些 Layer 或模組必須已完成）
> 輸出要求：可驗證的完成標準

---

## 1. 前置條件

[ ] 檢查清單：列出所有前置 Layer 必須完成的項目

---

## 2. 業務資料（直接複製）

若本 Layer 需要品牌資料、示例資料、預設配置，直接在此區塊提供，讓 AI 直接嵌入。

格式：
\`\`\`
| 項目 | 值 |
|---|---|
| 公司名稱 | XXX |
| ... | ... |
\`\`\`

---

## 3. 技術棧（嚴格遵守）

| 層次 | 技術 | 版本 |
|---|---|---|
| 前端 | React + TypeScript + Vite | 18 + 5 + 5 |
| 後端 | Hono + Cloudflare Worker | 4 + 3 |
| ... | ... | ... |

---

## 4. 檔案結構（必須精確）

提供本 Layer 需要生成/修改的檔案樹，標註：
- \`📄\` 新建檔案
- \`📝\` 修改檔案（已存在，需填充內容）
- \`📋\` 引用檔案（已存在，只讀取型別）

---

## 5. 核心實作規格

### 5.1 型別與資料契約
- 定義本 Layer 涉及的所有 interface/type
- 明確標註：哪些欄位必填、哪些可選

### 5.2 API 規格
- 端點列表：METHOD /path / description / auth required
- 輸入/輸出型別

### 5.3 組件/頁面規格
- 每個組件的 props interface
- 行為描述（非視覺設計）

### 5.4 邏輯規格
- 演算法/篩選/驗證規則
- 以偽代碼或數學表達式描述

---

## 6. 生成完成後的驗證步驟

將驗收標準轉化為編號檢查清單，讓 AI 生成後自我驗證：

\`\`\`
1. \`npm run build\` 成功（無 TypeScript 錯誤）
2. [具體功能 A] 正確運作（測試方式：...）
3. [具體功能 B] 正確運作（測試方式：...）
...
\`\`\`

---

## 7. 常見陷阱（本 Layer 特別關注）

從通用陷阱庫中選取本 Layer 最相關的 5-10 條，追加專案特有陷阱。

格式：
\`\`\`
### P<N>: <陷阱名稱>
- **現象**：...
- **根因**：...
- **Prompt 防禦**：「...」
\`\`\`
\`\`\`

---

## Layer 角色定義

| Layer | 名稱 | 職責 | 輸入 | 輸出 | 典型代碼行數 |
|---|---|---|---|---|---|
| Layer 1 | 骨架層 | 專案結構 + 型別 + API 路由 + 配置檔 | 需求文件 | 可編譯骨架 | 500-800 |
| Layer 2 | 前台層 | 所有使用者-facing 頁面與組件 | Layer 1 | 可交互前台 | 2000-4000 |
| Layer 3 | 後台層 | 管理介面與內部工具 | Layer 1+2 | 完整後台 | 1500-3000 |
| Layer 4 | 部署層 | CI/CD + 腳本 + 環境 | Layer 1-3 | 可部署配置 | 200-400 |

---

## Prompt 長度控制

| 指標 | 建議值 | 理由 |
|---|---|---|
| 總行數 | 200-400 行 | AI context window 的 sweet spot |
| 型別定義行數 | ≤ 100 行 | 避免型別區塊過長導致後面遺忘 |
| 驗證步驟數 | 8-15 條 | 可執行、可記憶 |
| 陷阱數 | 5-12 條 | 聚焦高頻問題 |

---

## 模組引用語法

在 Layer Prompt 中引用通用模組時，使用以下標記：

\`\`\`markdown
<!-- 引用通用模組：cloudflare-worker-pages -->
<!-- 引用通用模組：cms-common-types -->
<!-- 引用通用模組：hono-kv-auth -->
\`\`\`

這樣可以在未來的「Prompt 組裝器」中自動展開為對應模組內容。

---

## 變數替換語法

使用 \`{{VARIABLE_NAME}}\` 標記需要根據專案替換的值：

\`\`\`
{{PROJECT_NAME}}        → marveland
{{BRAND_NAME_CN}}       → 金晉物業代理
{{BRAND_NAME_EN}}       → Marveland Property Agency
{{KV_BINDING}}          → CMS_DATA
{{ADMIN_USERNAME}}      → Marveland
{{ADMIN_TOKEN}}         → marveland-admin-token
{{FALLBACK_PASSWORD}}   → admin360
{{PAGES_PROJECT_NAME}}  → marveland
{{CUSTOM_DOMAIN}}       → www.marveland.hk
\`\`\`

---

## 版本控制

每個 Layer Prompt 檔案頂部必須包含版本資訊：

\`\`\`markdown
# Layer N：...
> Prompt 版本：v1.0
> 最後更新：2026-01-15
> 對應專案：marveland
> 依賴 Layer：Layer 1 v1.0
\`\`\`
`;
